import { ReactComponent as IconInfo } from '@/assets/svgs/icon-info.svg';
import { SelectItem } from '@dextech-ai/dex-design-system';

// Helpers for driving a DS multi `Select` from the `items` arrays the pages keep
// in state (each item has an `id`, a `label` or `name`, and a `$selected` flag).

// Value of the "All" entry. It is never kept in the Select value: picking it selects
// every item, or clears them all when every item is already selected (the old "All"
// checkbox did the same). The DS multi Select ignores clicks that would deselect an
// option from inside the open list, so "All" has to work as a command, not a value.
export const SELECT_ALL_VALUE = '__all__';

export const getSelectedValues = (items) => (items ?? []).filter((item) => item.$selected).map((item) => item.id);

export const applySelectedValues = (items, values) => {
	const list = items ?? [];

	if (values.includes(SELECT_ALL_VALUE)) {
		const selectAll = !list.every((item) => item.$selected);
		return list.map((item) => ({ ...item, $selected: selectAll }));
	}

	return list.map((item) => ({ ...item, $selected: values.includes(item.id) }));
};

// Returns the SelectItem elements (not a wrapper component: the DS Select only
// collects SelectItem elements placed directly in SelectContent).
export const renderSelectItems = (items, { withAll = true, allLabel = 'All' } = {}) => {
	const list = items ?? [];
	const options = list.map((item) => (
		<SelectItem key={item.id} value={item.id}>{item.label || item.name}</SelectItem>
	));

	if (!withAll || list.length === 0) {
		return options;
	}

	return [<SelectItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>{allLabel}</SelectItem>, ...options];
};

// Single-select options shaped `{ name, value }` (the old components/Dropdown*
// lists). Missing entries are skipped.
export const renderValueOptions = (options) => (options ?? []).filter(Boolean).map((option) => (
	<SelectItem key={option.value} value={option.value}>{option.name}</SelectItem>
));

// Single-select options shaped `{ label, value }` (the DSV option lists).
export const renderLabelOptions = (options) => (options ?? []).filter(Boolean).map((option) => (
	<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
));

// The value to give a single Select: `value` when it is one of the options, otherwise
// null so the Select shows its placeholder (instead of the raw value). Not undefined:
// rc-select reads undefined as "uncontrolled" and keeps showing the last pick.
export const selectedOptionValue = (options, value) => (
	(options ?? []).some((option) => option?.value === value) ? value : null
);

// `filterOption` for a searchable Select: case-insensitive match on the option label.
export const filterByLabel = (input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

// Single-select options keyed by `id` (items shaped `{ id, name }`).
export const findItem = (items, id) => (items ?? []).find((item) => item?.id === id);

export const selectedItemId = (items, id) => (findItem(items, id) ? id : null);

// `optionRender` that puts an info button after the option label. The button opens the
// item's details (`onInfo(item)`) without picking the option; the selected value shown
// in the Select stays the plain label.
export const optionWithInfo = (items, onInfo) => (option) => {
	const label = option.data?.label ?? option.label;
	return (
		<span className="ds-select-option-with-info">
			<span className="ds-select-option-with-info__label">{label}</span>
			<button
				type="button"
				className="ds-select-option-with-info__button"
				aria-label={`About ${label}`}
				data-testid="dropdown_contributor_info_icon"
				onMouseDown={(event) => event.stopPropagation()}
				onClick={(event) => {
					event.stopPropagation();
					onInfo(findItem(items, option.value));
				}}
			>
				<IconInfo />
			</button>
		</span>
	);
};
